import { Component, ViewEncapsulation } from '@angular/core';
import { ImportsModule } from '../imports';
import { ActivatedRoute } from '@angular/router';
import { TreeNode } from 'primeng/api';
import { Chart } from 'chart.js/auto';

import { CustomerService } from '../services/customer.service';
import { StatusReportService } from '../services/status-report.service';
import { MessagingService } from '../services/messaging.service';
import { UserService } from '../services/user.service';

interface Column {
  field: string;
  header: string;
}
@Component({
  selector: 'app-status-report',
  standalone: true,
  imports: [ImportsModule],
  templateUrl: './status-report.component.html',
  styleUrl: './status-report.component.css',
  encapsulation: ViewEncapsulation.None
})
export class StatusReportComponent {
  showCustomers: boolean = false;
  customerName: string = "";
  customers: any[] = [];
  // userService.customer!: string;
  screenData: TreeNode[] = [];
  programData: TreeNode[] = [];
  screenTransLabel = "Screen Transformation";
  programTransLabel = "Program Transformation";
  PLTeamLabel = "TRFM Team";
  UATTeamLabel = "UAT Team";
  PITeamLabel = "POST Team";
  isBusy: boolean = true;
  cols: Column[] = [];
  custChartData: any;
  screenChartData: any;
  programChartData: any;
  screenPLTeamChartData: any;
  screenCustChartData: any;
  screenPIChartData: any;
  programPLTeamChartData: any;
  programCustChartData: any;
  programPIChartData: any;

  doneColor: string = "green";
  toDoColor: string = "lightgray";
  inProgressColor: string = "#97e08a";
  naColor: string = "gray";

  states!: any[];

  customerChartOptions: any = {
    indexAxis: 'y',
    plugins: {
      tooltip: {
        xAlign: 'center',
        yAlign: 'center',
        callbacks: {
          label: (context: any) => {
            return `${context.dataset.label}: ${context.formattedValue} %`;
          }
        }
      }
    },
    scales: {
      x: {
        min: 0,
        max: 100,
        stacked: true,
        border: {
          display: false,
        },
      },
      y: {
        stacked: true,
        border: {
          display: false,
        },
        ticks: {
          display: false,
        },
        grid: {
          display: false
        }
      }
    },
  };

  treeItemsChartOptions: any = {
    indexAxis: 'y',
    plugins: {
      tooltip: {
        xAlign: 'center',
        yAlign: 'center',
      }
    },
    scales: {
      x: {
        min: 0,
        max: 100,
        stacked: true,
        grid: {
          display: false
        },
        border: {
          display: false,
        },
        ticks: {
          display: false,
        }
      },
      y: {
        stacked: true,
        border: {
          display: false,
        },
        ticks: {
          display: false,
        },
        grid: {
          display: false
        }
      }
    },
  };

  doughtnutChartOptions: any = {
    cutout: '55%',
    plugins: {
      tooltip: false
    }
  };
  doughtnutChartCenterText: any[] = [{
    id: 'innerLabel',
    afterDatasetDraw(chart: any, args: any) {
      const { ctx } = chart;
      const meta = args.meta;
      const xCoor = meta.data[0].x + 1;
      const yCoor = meta.data[0].y + 7;
      let total = meta.total;
      let label: string = "N/A";
      if (chart.data.datasets[0].data[0] >= 0) {
        total = 100;
        label = Math.round((chart.data.datasets[0].data[0] / total) * 1000) / 10 + "%";
      }
      ctx.save();
      ctx.textAlign = 'center';
      ctx.font = '18px sans-serif';
      ctx.fillText(label, xCoor, yCoor);
      ctx.restore();
    }
  }];

  plTeamChartOptions: any = {
    cutout: '85%',
    plugins: {
      tooltip: false,
      title: {
        display: true,
        text: this.PLTeamLabel,
        position: 'bottom'
      }
    }
  };

  uatTeamChartOptions: any = {
    cutout: '85%',
    plugins: {
      tooltip: false,
      title: {
        display: true,
        text: this.UATTeamLabel,
        position: 'bottom'
      }
    }
  };

  piTeamChartOptions: any = {
    cutout: '85%',
    plugins: {
      tooltip: false,
      title: {
        display: true,
        text: this.PITeamLabel,
        position: 'bottom'
      }
    }
  };

  constructor(private route: ActivatedRoute, private customerService: CustomerService, private statusReportService: StatusReportService, public userService: UserService, private messagingService: MessagingService) {
    Chart.defaults.responsive = false;
    Chart.defaults.maintainAspectRatio = false;
    Chart.defaults.animation = false;
    Chart.defaults.resizeDelay = 100;
    Chart.defaults.plugins.legend.display = false;
    Chart.defaults.plugins.title.display = false;
  }

  ngOnInit() {
    this.showReport();
  }

  showReport() {
    if (!this.userService.customer) {
      return;
    }
    this.showChart();
  }

  showChart() {
    let customerTotals!: number;
    let screenWorkTotals!: number;
    let programWorkTotals!: number;
    let screenPLTeamTotals!: number;
    let screenCustTotals!: number;
    let screenPITotals!: number;
    let programPLTeamTotals!: number;
    let programCustTotals!: number;
    let programPITotals!: number;

    this.isBusy = true;
    this.statusReportService.getData(this.userService.customer).subscribe({
      next: (data: any) => {

        const columns = [];
        let doneState!: string;
        this.states = data.states.sort((a: any, b: any) => a.order - b.order);
        for (const state of this.states) {
          if (state.name === "Totals") {
            state.doneState = doneState;
            state.header = doneState || state.name;
          }
          if (state.isDoneState) {
            doneState = state.name;
            continue;
          }
          columns.push({ field: state.name, header: state.header || state.name, visible: false });
        }
        this.cols = columns;

        const screenWork = [];
        const programWork = [];

        // Build out TreeNode from the treeData
        for (const customer in data.treeData) {

          this.customerName = customer;
          const customerData: any = { label: customer };
          this.setStateCalcs(customerData, { "Totals": data.treeData[customer].states.Totals });
          if (customerData.Totals.complete != null) customerTotals = customerData.Totals.complete;

          const phases = data.treeData[customer].phases;

          for (const phase in phases) {
            const phaseData: any = { label: phase, level: 1 };
            this.setStateCalcs(phaseData, phases[phase].states);
            const phaseTotals: any = { data: phaseData, children: [], leaf: false, expanded: true };

            const isScreen = phase.match(/Screen/i);
            const isProgram = phase.match(/Program/i);

            if (isScreen) {
              this.screenTransLabel = phase;
              screenWork.push(phaseTotals);
              if (phaseData.Totals.complete != null) screenWorkTotals = phaseData.Totals.complete;
            } else if (isProgram) {
              this.programTransLabel = phase;
              programWork.push(phaseTotals);
              if (phaseData.Totals.complete != null) programWorkTotals = phaseData.Totals.complete;
            } else continue;

            const teams = phases[phase].teams;
            for (const team in teams) {
              const teamData: any = { label: team, level: 2 };
              this.setStateCalcs(teamData, teams[team].states);
              const teamTotals: any = { data: teamData, children: [], leaf: false, expanded: true };
              phaseTotals.children.push(teamTotals);

              const isPLTeam = team.match(/Transformation/i);
              const isUATTeam = team.match(/UAT/i);
              const isPITeam = !isPLTeam && !isUATTeam;

              if (teamData.Totals.complete != null) {
                if (isPLTeam) {
                  if (isScreen) screenPLTeamTotals = teamData.Totals.complete;
                  if (isProgram) programPLTeamTotals = teamData.Totals.complete;
                } else if (isUATTeam) {
                  if (isScreen) screenCustTotals = teamData.Totals.complete;
                  if (isProgram) programCustTotals = teamData.Totals.complete;
                } else if (isPITeam) {
                  if (isScreen) screenPITotals = teamData.Totals.complete;
                  if (isProgram) programPITotals = teamData.Totals.complete;
                }
              }

              const stages = teams[team].stages;
              for (const stage in stages) {
                const stageData = { label: stage, level: 3 };
                this.setStateCalcs(stageData, stages[stage].states);
                const stageTotals: any = { data: stageData, children: [], leaf: false, expanded: false };
                teamTotals.children.push(stageTotals);

                const worktypes = stages[stage].worktypes;
                for (const worktype in worktypes) {
                  const worktypeData = { label: worktype };
                  this.setStateCalcs(worktypeData, worktypes[worktype].states);
                  stageTotals.children.push({ data: worktypeData, leaf: true });
                }
              }
            }
          }
        }

        this.isBusy = false;

        this.custChartData = this.getBarChartData(customerTotals);
        this.screenChartData = this.getDountnutChartData(screenWorkTotals);
        this.programChartData = this.getDountnutChartData(programWorkTotals);
        this.screenPLTeamChartData = this.getDountnutChartData(screenPLTeamTotals);
        this.programPLTeamChartData = this.getDountnutChartData(programPLTeamTotals);
        this.screenCustChartData = this.getDountnutChartData(screenCustTotals);
        this.programCustChartData = this.getDountnutChartData(programCustTotals);
        this.screenPIChartData = this.getDountnutChartData(screenPITotals);
        this.programPIChartData = this.getDountnutChartData(programPITotals);

        this.screenData = screenWork;
        this.programData = programWork;
      },
      error: (error: any) => {
        this.isBusy = false;
        this.messagingService.showHttpError("error", error.error, "Failed to load data.", error);
      }
    });
  }

  showScreenDetails(data: any, state: any) {
    this.showDetails("screen", data, state);
  }
  showProgramDetails(data: any, state: any) {
    this.showDetails("program", data, state);
  }
  showDetails(type: string, data: any, state: any) {
    state = this.states.find(i => i.name === state);
    if (state.doneState) state = this.states.find(i => i.name === state.doneState);

    let phase!: string;
    let team!: string;
    let stage!: string;
    let worktype!: string;

    // get all of the parents of this data item, and then go and get the details
    const getParent = (item: any) => {
      if (!worktype) worktype = item.label;
    };

    // if (data.parent)


  }

  getBarChartData(completed?: number) {
    completed = completed || 0;
    return {
      labels: [""],
      datasets: [
        {
          label: "Done",
          data: [completed],
          backgroundColor: completed === 100 ? this.doneColor : this.inProgressColor,
          hoverBackgroundColor: completed === 100 ? this.doneColor : this.inProgressColor,
        },
        {
          label: "To Do",
          data: [100 - completed],
          backgroundColor: this.toDoColor,
          hoverBackgroundColor: this.toDoColor
        }
      ]
    };
  }

  getDountnutChartData(completed?: number) {
    return {
      labels: ["Done", "To Do"],
      datasets: [
        {
          data: [completed, completed == null ? 100 : 100 - completed],
          backgroundColor: [completed === 100 ? this.doneColor : this.inProgressColor, completed == null ? this.naColor : this.toDoColor],
          hoverBackgroundColor: [completed === 100 ? this.doneColor : this.inProgressColor, completed == null ? this.naColor : this.toDoColor]
        }
      ]
    };
  }

  setStateCalcs(dataItem: any, statesData: any) {
    const totalEstimated = statesData.Totals.estimated;
    let showChart = false;
    for (let state in statesData) {
      const stateData = statesData[state];
      const stateItem = this.states.find(i => i.name === state);

      const dataState: any = dataItem[state] = { count: stateData.totalItems, complete: null, estimated: 0, remaining: 0, worked: 0 };
      dataState.estimated = stateData.estimated;
      dataState.remaining = stateData.remaining;
      dataState.worked = stateData.worked;;

      // We want the "Done" column to represent overall Totals for all states
      //  When it comes to time left to work
      //  But when it comes to count -- we want to show the right "Done" record counts
      if (state === "Totals" && dataItem[stateItem.doneState]) {
        dataState.count = dataItem[stateItem.doneState].count;
      }

      if (dataState.count) {
        showChart = true;
        if (!dataState.remaining && (dataState.estimated || totalEstimated) == totalEstimated) dataState.complete = 100;
        if (dataState.complete == null) {
          dataState.complete = ((dataState.remaining || dataState.estimated || totalEstimated) / totalEstimated) * 100;
          if (state === "Totals") dataState.complete = 100 - dataState.complete;
        }
      }

    }

    if (showChart) dataItem.Totals.chartData = this.getBarChartData(dataItem.Totals.complete);
  }
};